// Bio page analytics - detailed analytics for a single bio page
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import {
  FiArrowLeft, FiExternalLink, FiCopy,
  FiMousePointer, FiLink, FiGlobe, FiMonitor, FiSmartphone, FiDownload, FiMail,
} from "react-icons/fi";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import { getBaseUrl } from "../utils/domain";
import "../styles/Analytics.css";

const COLORS = [
  "#4f46e5", "#059669", "#d97706", "#dc2626",
  "#7c3aed", "#0891b2", "#be185d", "#65a30d",
];

const DEVICE_ICONS = {
  Desktop: <FiMonitor size={14} />,
  Mobile: <FiSmartphone size={14} />,
  Tablet: <FiSmartphone size={14} />,
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{label}</div>
      <div className="chart-tooltip-value">{payload[0].value.toLocaleString()} {payload[0].dataKey === 'clicks' ? 'clicks' : 'views'}</div>
    </div>
  );
}

function BarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{payload[0].payload.name || payload[0].payload.title}</div>
      <div className="chart-tooltip-value">{payload[0].value.toLocaleString()} clicks</div>
    </div>
  );
}

export default function BioPageAnalytics() {
  const { id: bioPageId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const qrRef = useRef(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getBioPageAnalytics(bioPageId, period);
      setData(result);
    } catch (err) {
      console.error("Fetch bio analytics error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bioPageId, period]);

  useEffect(() => {
    document.title = "Bio Page Analytics - LinkSquire";
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getFullUrl = () => {
    if (!data?.bio_page) return "";
    const base = getBaseUrl();
    return `${base}/${data.bio_page.slug}`;
  };

  const downloadQrCode = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${(data?.bio_page?.slug || "bio-page")}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  if (loading) {
    return (
      <div className="analytics-page loading-state">
        <div className="spinner" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page error-state">
        <p>Error: {error}</p>
        <button className="btn-primary" onClick={() => navigate("/bio")}>
          Back to Bio Pages
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { bio_page, page_views = 0, link_clicks = 0, total_events = 0, lead_count = 0, link_stats = [], by_country = [], by_device = [], by_browser = [], by_referrer = [], by_date = [], lead_by_date = [], recent_leads = [] } = data;
  const fullUrl = getFullUrl();
  const totalDeviceClicks = by_device.reduce((s, d) => s + d.count, 0);

  const stats = [
    { label: "Page Views", value: page_views, icon: <FiMousePointer />, color: "#4f46e5", sub: "Total visits" },
    { label: "Link Clicks", value: link_clicks, icon: <FiLink />, color: "#059669", sub: "Clicks on links" },
    { label: "Total Events", value: total_events, icon: <FiMousePointer />, color: "#d97706", sub: "All interactions" },
    { label: "Leads", value: lead_count, icon: <FiMail />, color: "#db2777", sub: "Email captures" }
  ];

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-page-header">
        <div className="analytics-page-header-left">
          <button className="btn-back" onClick={() => navigate("/bio")}>
            <FiArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 className="analytics-page-title">{bio_page.title}</h1>
            <div className="analytics-link-meta">
              <span className="analytics-short-link">
                {fullUrl.replace(/^https?:\/\//, "")}
                <button
                  className="btn-icon-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(fullUrl);
                    addToast("Copied!", "success");
                  }}
                >
                  <FiCopy size={11} />
                </button>
              </span>
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="analytics-dest-link"
              >
                View bio page <FiExternalLink size={11} />
              </a>
            </div>
          </div>
        </div>
        <div className="analytics-page-header-right">
          <div className="period-selector">
            {["1d", "7d", "30d"].map((p) => (
              <button
                key={p}
                className={period === p ? "active" : ""}
                onClick={() => setPeriod(p)}
              >
                {p === "1d" ? "24H" : p === "7d" ? "7D" : "30D"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="analytics-stat-cards">
        {stats.map((s) => (
          <div key={s.label} className="analytics-stat-card">
            <div className="analytics-stat-icon" style={{ background: `${s.color}12`, color: s.color }}>
              {s.icon}
            </div>
            <div className="analytics-stat-content">
              <span className="analytics-stat-value">{s.value.toLocaleString()}</span>
              <span className="analytics-stat-label">{s.label}</span>
            </div>
            <span className="analytics-stat-sub">{s.sub}</span>
          </div>
        ))}
      </div>

      {/* Share Tools - QR Code */}
      <div className="analytics-card analytics-card-wide">
        <div className="analytics-card-header">
          <h3>Share Tools</h3>
        </div>
        <div className="qr-share-grid">
          <div ref={qrRef} className="qr-code-container">
            <QRCodeCanvas value={fullUrl} size={180} includeMargin fgColor="#37352f" bgColor="#ffffff" />
          </div>
          <div>
            <p style={{ marginBottom: 14, color: "var(--text-secondary)" }}>
              Use the QR code for print, event badges, or offline collateral.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn-secondary" onClick={() => {
                navigator.clipboard.writeText(fullUrl);
                addToast("URL copied to clipboard", "success");
              }}>
                <FiCopy size={13} /> Copy URL
              </button>
              <button className="btn-secondary" onClick={downloadQrCode}>
                <FiDownload size={13} /> Download PNG
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Events Over Time */}
      {by_date?.length > 0 && (
        <div className="analytics-card analytics-card-wide">
          <div className="analytics-card-header">
            <h3>Page Views Over Time</h3>
            <span className="analytics-card-badge">{page_views?.toLocaleString()} views</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={by_date}>
              <defs>
                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0ef" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2} fill="url(#colorViews)" dot={false} activeDot={{ r: 4, fill: "#4f46e5" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Leads Over Time */}
      {lead_by_date?.length > 0 && (
        <div className="analytics-card analytics-card-wide">
          <div className="analytics-card-header">
            <h3>Leads Over Time</h3>
            <span className="analytics-card-badge">{lead_count?.toLocaleString()} leads</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={lead_by_date}>
              <defs>
                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#db2777" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#db2777" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0ef" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="count" stroke="#db2777" strokeWidth={2} fill="url(#colorLeads)" dot={false} activeDot={{ r: 4, fill: "#db2777" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Link Performance */}
      {link_stats?.length > 0 && (
        <div className="analytics-card analytics-card-wide">
          <div className="analytics-card-header">
            <h3>Link Performance</h3>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(240, link_stats.length * 35)}>
            <BarChart data={link_stats} layout="vertical" barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0ef" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#9b9a97" }} axisLine={false} tickLine={false} />
              <YAxis 
                type="category" 
                dataKey="title" 
                width={120} 
                tick={{ fontSize: 11, fill: "#37352f" }} 
                axisLine={false} 
                tickLine={false}
                tickFormatter={(val) => val.length > 20 ? val.slice(0, 17) + "..." : val}
              />
              <Tooltip content={<BarTooltip />} />
              <Bar dataKey="clicks" fill="#4f46e5" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Two column grid - Countries and Recent Leads */}
      <div className="analytics-grid-2">
        {/* Countries */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3><FiGlobe size={14} /> Top Countries</h3>
          </div>
          {by_country.length > 0 ? (
            <div className="analytics-list">
              {by_country.slice(0, 8).map((c, i) => {
                const pct = page_views > 0 ? ((c.count / page_views) * 100).toFixed(1) : 0;
                return (
                  <div key={c.name} className="analytics-list-item">
                    <span className="analytics-list-rank">{i + 1}</span>
                    <span className="analytics-list-name">{c.name}</span>
                    <div className="analytics-list-bar-track">
                      <div className="analytics-list-bar" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span className="analytics-list-count">{c.count.toLocaleString()}</span>
                    <span className="analytics-list-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="chart-empty-state small">
              <FiGlobe size={20} />
              <p>No location data yet</p>
            </div>
          )}
        </div>

        {/* Recent Leads */}
        {recent_leads?.length > 0 && (
          <div className="analytics-card">
            <div className="analytics-card-header">
              <h3><FiMail size={14} /> Recent Leads</h3>
            </div>
            <div className="analytics-list">
              {recent_leads.slice(0, 8).map((lead, i) => (
                <div key={lead.id} className="analytics-list-item">
                  <span className="analytics-list-rank">{i + 1}</span>
                  <div className="analytics-lead-info">
                    <span className="analytics-lead-name">{lead.name || "Unknown"}</span>
                    <span className="analytics-lead-email">{lead.email}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Two column grid - Devices and Browsers */}
      <div className="analytics-grid-2">
        {/* Devices */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Devices</h3>
          </div>
          {by_device.length > 0 ? (
            <div className="analytics-device-grid">
              {by_device.map((d, i) => {
                const pct = totalDeviceClicks > 0 ? ((d.count / totalDeviceClicks) * 100).toFixed(0) : 0;
                return (
                  <div key={d.name} className="analytics-device-card">
                    <div className="analytics-device-icon" style={{ color: COLORS[i % COLORS.length] }}>
                      {DEVICE_ICONS[d.name] || <FiMonitor size={14} />}
                    </div>
                    <div className="analytics-device-info">
                      <span className="analytics-device-name">{d.name}</span>
                      <span className="analytics-device-pct">{pct}%</span>
                    </div>
                    <div className="analytics-device-bar-track">
                      <div className="analytics-device-bar" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span className="analytics-device-count">{d.count.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="chart-empty-state small">
              <p>No device data yet</p>
            </div>
          )}
        </div>

        {/* Browsers */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Browsers</h3>
          </div>
          {by_browser.length > 0 ? (
            <div className="analytics-donut-container">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={by_browser.slice(0, 6)}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {by_browser.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<BarTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="analytics-legend">
                {by_browser.slice(0, 6).map((b, i) => (
                  <div key={b.name} className="analytics-legend-item">
                    <span className="analytics-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="analytics-legend-label">{b.name}</span>
                    <span className="analytics-legend-value">{b.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="chart-empty-state small">
              <p>No browser data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Referrers */}
      {by_referrer?.length > 0 && (
        <div className="analytics-card analytics-card-wide">
          <div className="analytics-card-header">
            <h3>Top Referrers</h3>
          </div>
          <div className="analytics-list">
            {by_referrer.slice(0, 10).map((r, i) => {
              const pct = page_views > 0 ? ((r.count / page_views) * 100).toFixed(1) : 0;
              return (
                <div key={r.name} className="analytics-list-item">
                  <span className="analytics-list-rank">{i + 1}</span>
                  <span className="analytics-list-name">{r.name || "Direct"}</span>
                  <div className="analytics-list-bar-track">
                    <div className="analytics-list-bar" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                  <span className="analytics-list-count">{r.count.toLocaleString()}</span>
                  <span className="analytics-list-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
